<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue'
import type { Ref } from 'vue'
import { CalendarDate } from '@internationalized/date'
import type { DateValue } from '@internationalized/date'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { Calendar } from './calendar'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'

interface Props {
  modelValue?: string | Date
  label?: string | false
  placeholder?: string
  disabled?: boolean
  required?: boolean
  minDate?: string
  maxDate?: string
  /** Show month and year dropdowns instead of prev/next buttons. Useful for date-of-birth inputs. */
  yearMonthDropdown?: boolean
  /** Year to pre-scroll the calendar to when no value is set. Useful for date-of-birth inputs. */
  defaultYear?: number
  /** Hide the clear button beneath the calendar grid. */
  hideClear?: boolean
  /** Keep the calendar dropdown open after a date is selected. */
  stayOpenOnSelect?: boolean
  validate?: (value: string) => boolean | string
  validateOnBlur?: boolean
  dataTestId?: string
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  disabled: false,
  required: false,
  minDate: '1900-01-01',
  maxDate: '2100-12-31',
  yearMonthDropdown: true,
  stayOpenOnSelect: true,
  validate: () => true,
  validateOnBlur: false,
  ariaLabel: 'Date picker',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
}>()

const inputId = shallowRef(`am3-input-${uuidv4()}`)
const isOpen = shallowRef(false)
const internalIsoValue = shallowRef('')

const buttonBorderClasses = 'rounded-md border-surface-dark hover:border-brand'
const disabledClasses = 'text-disabled cursor-not-allowed hover:border-surface-dark hover:outline-none text-white'
const slottedClasses = 'focus:outline-transparent focus-visible:outline-0 focus-visible:border-0 bg-transparent border-0'

const computedPlaceholder = computed(() =>
  props.placeholder ?? (props.label ? `Select ${String(props.label).toLowerCase()}` : 'Select date'),
)

const computedDataTestId = computed(() =>
  props.dataTestId ?? ((typeof props.label === 'string' ? props.label : '') || 'date-input'),
)

// Form validation
const {
  validationState,
  runValidation: _runValidation,
  resetValidation,
  validateOnBlur: validateOnBlurRef,
  handleBlur: _handleBlur,
} = useFormValidation(props.validate, props.modelValue, props.required)

;(validateOnBlurRef as unknown as Ref<boolean>).value = props.validateOnBlur

const runValidation = () => _runValidation(props.modelValue, props.required)

const handleBlur = () => {
  _handleBlur(props.modelValue)
  validationState.value = props.validate(props.modelValue as string)
}

// Date formatting (display only — always shows MMM D, YYYY)
function formatDisplayDate(date: Date): string {
  if (isNaN(date.getTime())) return ''
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

// Initialise internal ISO value from modelValue prop
function initFromModelValue() {
  if (!props.modelValue) {
    internalIsoValue.value = ''
    return
  }

  if (props.modelValue instanceof Date) {
    internalIsoValue.value = props.modelValue.toISOString().split('T')[0]
    return
  }

  const isoMatch = (props.modelValue as string).match(/(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) {
    internalIsoValue.value = isoMatch[0]
    return
  }

  const monthMap: Record<string, string> = {
    Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
    Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
  }
  const mmmMatch = (props.modelValue as string).match(/^(\w{3})\s+(\d{1,2}),\s+(\d{4})$/)
  if (mmmMatch) {
    const [, monthName, day, year] = mmmMatch
    const monthNum = monthMap[monthName]
    if (monthNum) {
      internalIsoValue.value = `${year}-${monthNum}-${day.padStart(2, '0')}`
      return
    }
  }

  const date = new Date(props.modelValue as string)
  if (!isNaN(date.getTime())) {
    internalIsoValue.value = date.toISOString().split('T')[0]
  }
}

initFromModelValue()
watch(() => props.modelValue, initFromModelValue)

// Formatted display string
const formattedValue = computed(() => {
  if (!internalIsoValue.value) return ''
  const date = new Date(`${internalIsoValue.value}T00:00:00`)
  return formatDisplayDate(date)
})

// ISO string → CalendarDate for Calendar v-model
function isoToCalendarDate(iso: string): CalendarDate | undefined {
  if (!iso) return undefined
  const [y, m, d] = iso.split('-').map(Number)
  return new CalendarDate(y, m, d)
}

const calendarValue = computed(() => isoToCalendarDate(internalIsoValue.value))
const minCalendarDate = computed(() => isoToCalendarDate(props.minDate ?? '1900-01-01'))
const maxCalendarDate = computed(() => isoToCalendarDate(props.maxDate ?? '2100-12-31'))
const calendarPlaceholder = computed(() =>
  calendarValue.value ?? (props.defaultYear ? new CalendarDate(props.defaultYear, 1, 1) : undefined),
)

function onSelect(value: DateValue | undefined) {
  if (!value) return
  const iso = `${value.year}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`
  internalIsoValue.value = iso
  emit('update:modelValue', iso)
  emit('change', iso)
  handleBlur()
  if (!props.stayOpenOnSelect) isOpen.value = false
}

function toggleOpen() {
  if (!props.disabled) isOpen.value = !isOpen.value
}

function onClear() {
  internalIsoValue.value = ''
  emit('update:modelValue', '')
  emit('change', '')
  isOpen.value = false
  resetValidation()
}

defineExpose({ runValidation, resetValidation })
</script>

<template>
  <div class="am3-input-container text-sm">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    />

    <div
      :id="inputId"
      class="am3-input-wrapper relative flex h-11 flex-row items-center justify-between rounded-md cursor-pointer border"
      :class="[
        disabled ? 'disabled' : '',
        ($slots.right || $slots.left) ? 'focus-within:outline-brand bg-surface-light px-3' : '',
        disabled ? disabledClasses : 'bg-surface-light',
        (validationState === false || typeof validationState === 'string') ? 'error border border-solid border-[var(--font-colour-warning)]' : buttonBorderClasses,
      ]"
      :data-test-id="generateDataTestId(computedDataTestId, 'input-wrapper')"
      @click="toggleOpen"
    >
      <div
        v-if="$slots.left"
        class="am3-input-slot flex items-center justify-center"
      >
        <slot name="left" />
      </div>

      <input
        :id="`${inputId}-display`"
        type="text"
        class="am3-input text-sm p-2.5 w-full h-full cursor-pointer rounded-md outline-none"
        :class="[
          ($slots.right || $slots.left)
            ? slottedClasses
            : 'focus:outline-brand focus:outline-offset-0',
          disabled ? disabledClasses : 'bg-surface-light hover:border-brand',
          modelValue ? 'text-primary' : 'text-secondary',
        ]"
        :value="formattedValue"
        :placeholder="computedPlaceholder"
        :disabled="disabled"
        :data-test-id="generateDataTestId(computedDataTestId, 'input')"
        :aria-label="ariaLabel || `Date input - ${inputId}`"
        readonly
      >

      <div
        v-if="$slots.right"
        class="am3-input-slot flex items-center justify-center"
      >
        <slot name="right" />
      </div>
    </div>

    <AM3DropdownPanel
      v-model="isOpen"
      :target="inputId"
      fit-content
      position="bottom-right"
    >
      <Calendar
        :model-value="calendarValue"
        :placeholder="calendarPlaceholder"
        :min-value="minCalendarDate"
        :max-value="maxCalendarDate"
        :caption-layout="yearMonthDropdown ? 'dropdown' : 'buttons'"
        :hide-clear="hideClear"
        @update:model-value="onSelect"
        @clear="onClear"
      />
    </AM3DropdownPanel>

    <AM3InputError :error="validationState" />
  </div>
</template>
